import { ReactNode } from 'react';
import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import { MessageHeartCircleIcon, SendIcon } from 'components/Icons';

import {
  Box,
  Button,
  Column,
  LabeledInput,
  overlayEscape,
  ResponsiveOverlay,
  Row,
} from 'components/Blocks';
import { TextareaWithFiles } from 'components/Inputs';

import { SVGAnimation } from 'components/SVGAnimation';
import success from 'assets/animations/success.json';

import { useFileInput, useFormControlV2 } from 'hooks';

const TEXT_MIN_LENGTH = 10;
const TITLE_MIN_LENGTH = 5;

interface BugReportModalProps {
  trigger: ReactNode;
}

export function NewSuggestionModal({ trigger }: BugReportModalProps) {
  const { t } = useLang();

  const fileInput = useFileInput();

  const [{ text, title, sent }, { handleChange, resetDefault, dispatch }] = useFormControlV2({
    text: '',
    title: '',
    sent: false,
  });

  const isDisabled = text.length <= TEXT_MIN_LENGTH || title.length <= TITLE_MIN_LENGTH;

  const sendSuggestion = actions.feedback.createSuggestion.use({
    block: isDisabled,

    onSuccess: () => {
      dispatch({ sent: true });
      fileInput.clearFiles();
    },

    params: {
      files: fileInput.files.map((f) => f.file),
      text,
      title,
    },
  });

  return (
    <ResponsiveOverlay largeClassName="min-w-[520px]" trigger={trigger}>
      {sent ? (
        <Column alignItems="center" gap="2">
          <SVGAnimation animationData={success} size={120} />

          <h2 className="text-success">{t('success')}!</h2>

          <p>{t('suggestion-received')}</p>

          <Button variant="ghost" onClick={resetDefault}>
            <MessageHeartCircleIcon />
            {t('suggest-again')}
          </Button>
        </Column>
      ) : (
        <>
          <Box className="mx-auto size-16 rounded-full border-primary bg-primary/20 p-2">
            <MessageHeartCircleIcon size={36} color="primary-light" />
          </Box>

          <Column gap="1" alignItems="center">
            <h2 className="mx-auto text-center text-3xl text-primary">{t('we-want-hear-you')}</h2>

            <p className="text-muted-foreground/50">{t('suggest-best-feature')}</p>
          </Column>

          <LabeledInput
            label={t('title')}
            value={title}
            name="title"
            onChange={handleChange}
            placeholder={t('max-65-charts-title')!}
            className="placeholder:text-xs placeholder:italic"
          />

          <TextareaWithFiles
            className="min-h-24"
            label={t('what-can-be-improved')}
            containerClassName="mb-2"
            name="text"
            onChange={handleChange}
            {...fileInput}
          />

          <Row justifyContent="space-between">
            <Button variant="ghost" to="mySuggestions" onClick={() => overlayEscape()}>
              <MessageHeartCircleIcon />
              {t('my-suggestions')}
            </Button>

            <Button variant="outlined" {...sendSuggestion.buttonProps} disabled={isDisabled}>
              <SendIcon />
              {t('suggest')}!
            </Button>
          </Row>
        </>
      )}
    </ResponsiveOverlay>
  );
}
